import { useEffect, useState, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { FaHeart, FaShareAlt, FaFacebook, FaWhatsapp } from "react-icons/fa";
import { SiGmail } from "react-icons/si";
import { API_BASE_URL, CART_API_URL } from "../config/api";
import Seo from "../Components/Seo";

const ProductDetails = () => {
  const { productId } = useParams();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showShare, setShowShare] = useState(false);
  const shareRef = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [cartProductIds, setCartProductIds] = useState([]);
  const [cartAnimating, setCartAnimating] = useState(false);
  const userMobile = localStorage.getItem("userMobileNumber") || "";

  const images = useMemo(() => {
    if (!product) return [];
    const list = [
      product.Image1,
      product.Image2,
      product.Image3,
      product.Image4,
      product.Image5,
      product.Image6,
    ].filter(Boolean);
    // Fallback to single image if only Image1 exists
    return list.length ? list : [product.Image1].filter(Boolean);
  }, [product]);

  // Preload images to make carousel navigation instant
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  // 🔹 API CALL
  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/Product/GetProductById?id=${productId}`
        );
        const data = await response.json();

        // API returns array → take first object
        setProduct(data?.[0] || null);
      } catch (err) {
        setError("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productId]);

  // 🔹 Close share on outside click or Esc
  useEffect(() => {
    const onClickOutside = (e) => {
      if (showShare && shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShare(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setShowShare(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [showShare]);

  // 🔹 TIME AGO CALCULATION
  const getTimeAgo = (dateString) => {
    if (!dateString) return "";

    const createdDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - createdDate;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 60) return `${minutes} minutes ago`;
    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  // Normalize product fields to generic contract
  const normalizeProduct = (p) => ({
    id: p?.id ?? p?.Id ?? productId,
    name: p?.name ?? p?.ProductName ?? "",
    description: p?.description ?? p?.Description ?? "",
    price: p?.price ?? p?.Price ?? "",
    image: p?.image ?? p?.Image1 ?? "",
  });

  // Build canonical product URL
  const getCanonicalProductUrl = (id) => {
    const base = process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin;
    return `${base}/product-details/${id}`;
  };

  // Facebook: open sharer with canonical URL (prefill), let FB fetch OG preview
  const openFacebookShare = (p) => {
    const prod = normalizeProduct(p);
    const pageUrl = getCanonicalProductUrl(prod.id);
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // WhatsApp: craft message for both mobile and desktop
  const openWhatsAppShare = (p) => {
    const prod = normalizeProduct(p);
    const pageUrl = getCanonicalProductUrl(prod.id);
    const lines = [
      prod.name && `Product: ${prod.name}`,
      prod.price !== "" && `Price: ₹${prod.price}`,
      prod.description && `Description: ${prod.description}`,
      prod.image && `Image: ${prod.image}`,
      `Details: ${pageUrl}`,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));

    const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
      navigator.userAgent
    );
    const waUrl = isMobile
      ? `https://wa.me/?text=${text}`
      : `https://web.whatsapp.com/send?text=${text}`;

    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  // Gmail: mailto with subject and body
  const openGmailShare = (p) => {
    const prod = normalizeProduct(p);
    const pageUrl = getCanonicalProductUrl(prod.id);
    const subject = encodeURIComponent(`Check out: ${prod.name}`);
    const bodyLines = [
      prod.name && `Product: ${prod.name}`,
      prod.price !== "" && `Price: ₹${prod.price}`,
      prod.description && `Description: ${prod.description}`,
      prod.image && `Image: ${prod.image}`,
      `Details: ${pageUrl}`,
    ].filter(Boolean);
    const body = encodeURIComponent(bodyLines.join("\n"));
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, "_blank", "noopener,noreferrer");
  };

  // Helper: set Open Graph meta tags for Facebook
  const setOgTag = (property, content) => {
    if (!content) return;
    let tag = document.querySelector(`meta[property="${property}"]`);
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", property);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };
  const removeOgTag = (property) => {
    const tag = document.querySelector(`meta[property="${property}"]`);
    if (tag) document.head.removeChild(tag);
  };

  // When product loads, set OG tags to match canonical URL
  useEffect(() => {
    if (!product) return;
    const ogTitle = product?.ProductName || "Product";
    const ogDesc = product?.Description || "";
    const ogImage = product?.Image1 || "";
    const ogUrl = getCanonicalProductUrl(product?.Id || productId);

    setOgTag("og:title", ogTitle);
    setOgTag("og:description", ogDesc);
    setOgTag("og:image", ogImage);
    setOgTag("og:url", ogUrl);

    return () => {
      removeOgTag("og:title");
      removeOgTag("og:description");
      removeOgTag("og:image");
      removeOgTag("og:url");
    };
  }, [product, productId]);

  // Refresh cart count placeholder
  const refreshCartCount = () => {
    // no-op placeholder; wire to header/global cart badge if available
  };

  // 🔹 Load user's cart to set initial add/remove state
  useEffect(() => {
    const loadCart = async () => {
      if (!userMobile) return;
      try {
        const res = await fetch(
          `${CART_API_URL}/Cart/GetCartProducts?mobileNo=${userMobile}`
        );
        if (!res.ok) throw new Error("Cart fetch failed");
        const data = await res.json();
        const ids =
          data?.products?.map((p) => p?.id ?? p?.Id).filter(Boolean) || [];
        setCartProductIds(ids);
      } catch (e) {
        console.log("Error loading cart", e);
      }
    };
    loadCart();
  }, [userMobile]);

  // Handle add/remove cart toggle
  const handleCartToggle = async (id) => {
    if (!userMobile || !id) return;
    const inCart = cartProductIds.includes(id);
    const action = inCart ? "removeProduct" : "addProduct";
    try {
      setCartAnimating(true);
      const res = await fetch(`${CART_API_URL}/Cart/UpdateCart`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNo: userMobile,
          productId: id,
          action,
        }),
      });
      if (!res.ok) throw new Error("Cart update failed");
      setCartProductIds((prev) =>
        inCart ? prev.filter((pid) => pid !== id) : [...prev, id]
      );
      refreshCartCount();
      setTimeout(() => setCartAnimating(false), 250);
    } catch (e) {
      console.log("Error updating cart", e);
      setCartAnimating(false);
    }
  };

  if (loading) {
    return <div style={styles.center}>Loading product...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  if (!product) {
    return <div style={styles.center}>No product found</div>;
  }

  const onShareClick = () => setShowShare((v) => !v);
  const onShareAction = (type) => {
    if (type === "Facebook") {
      openFacebookShare(product);
      setShowShare(false);
      return;
    }
    if (type === "WhatsApp") {
      openWhatsAppShare(product);
      setShowShare(false);
      return;
    }
    if (type === "Gmail") {
      openGmailShare(product);
      setShowShare(false);
      return;
    }
    // Placeholder actions; integrate real share URLs or Web Share API later
    console.log(`Share via ${type}`);
    setShowShare(false);
  };
  const dialSeller = () => {
    if (!product?.MobileNo) return;
    window.location.href = `tel:${product.MobileNo}`;
  };

  return (
    <div style={styles.page}>
      <Seo
        title={product?.ProductName || "Product"}
        description={(product?.Description || `${product?.ProductName} on AIO India`).slice(0, 160)}
        path={`/product-details/${product?.Id || productId}`}
        image={product?.Image1 || ""}
        schema={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product?.ProductName || "Product",
          description: (product?.Description || "").slice(0, 300),
          image: product?.Image1 || "https://www.facebookindia.in/logo512.png",
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: Number(product?.Price) || 0,
            availability: "https://schema.org/InStock",
            seller: { "@type": "Organization", name: "AIO India" },
          },
        }}
      />
      <div style={styles.container}>
        {/* 🔹 LEFT SECTION */}
        <div style={styles.left}>
          <div style={styles.imageWrapper}>
            <div style={{ position: "relative", overflow: "hidden" }}>
              {images.length > 0 && (
                <img
                  src={images[currentImageIndex]}
                  alt={product?.ProductName || "Product"}
                  style={styles.image}
                  loading="eager"
                />
              )}
              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous image"
                    onClick={() => setCurrentImageIndex((i) => (i === 0 ? images.length - 1 : i - 1))}
                    style={styles.carouselArrowLeft}
                  >
                    ‹
                  </button>
                  <button
                    aria-label="Next image"
                    onClick={() => setCurrentImageIndex((i) => (i === images.length - 1 ? 0 : i + 1))}
                    style={styles.carouselArrowRight}
                  >
                    ›
                  </button>
                </>
              )}
            </div>

            {/* ICONS */}
            <div style={styles.icons}>
              <FaHeart style={styles.icon} title="Add to Wishlist" />
              <div style={{ position: "relative" }} ref={shareRef}>
                <FaShareAlt
                  style={styles.icon}
                  title="Share Product"
                  onClick={onShareClick}
                  role="button"
                  tabIndex={0}
                />
                {showShare && (
                  <div style={styles.sharePopup}>
                    <button style={styles.shareItem} onClick={() => onShareAction("Facebook")} aria-label="Share to Facebook">
                      <FaFacebook color="#1877F2" size={18} />
                      <span style={styles.shareText}>Facebook</span>
                    </button>
                    <button style={styles.shareItem} onClick={() => onShareAction("WhatsApp")} aria-label="Share to WhatsApp">
                      <FaWhatsapp color="#25D366" size={18} />
                      <span style={styles.shareText}>WhatsApp</span>
                    </button>
                    {/* <button style={styles.shareItem} onClick={() => onShareAction("Gmail")} aria-label="Share via Gmail">
                      <SiGmail color="#EA4335" size={18} />
                      <span style={styles.shareText}>Gmail</span>
                    </button> */}
                  </div>
                )}
              </div>
            </div>

            {images.length > 1 && (
              <div style={styles.dotsRow}>
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    aria-label={`Go to image ${idx + 1}`}
                    onClick={() => setCurrentImageIndex(idx)}
                    style={{
                      ...styles.dot,
                      background: idx === currentImageIndex ? "#333" : "#ccc",
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div style={styles.buttonGroup}>
            <button
              style={{
                ...styles.button,
                ...styles.cartBtn,
                transform: cartAnimating ? "scale(1.03)" : "scale(1)",
                transition: "transform 0.2s ease",
              }}
              onClick={() => handleCartToggle(product?.Id || product?.id || productId)}
            >
              {cartProductIds.includes(product?.Id || product?.id || productId)
                ? "REMOVE FROM CART"
                : "ADD TO CART"}
            </button>
            <button style={{ ...styles.button, ...styles.buyBtn }}>
              BUY NOW
            </button>
          </div>
        </div>

        {/* 🔹 RIGHT SECTION */}
        <div style={styles.right}>
          <h1 style={styles.title}>{product?.ProductName}</h1>

          <p style={styles.price}>₹ {product?.Price}</p>

          {product?.Quantity > 0 && (
            <p style={styles.quantity}>
              Quantity: {product.Quantity}
            </p>
          )}

          <p style={styles.desc}>{product?.Description}</p>

          <p style={styles.mobile}>
            <button
              style={styles.phoneButton}
              onClick={dialSeller}
              aria-label="Call seller"
              type="button"
            >
              📞
            </button>
            <span style={{ marginLeft: "8px" }}>
              Seller Contact: <b>{product?.MobileNo}</b>
            </span>
          </p>

          <p style={styles.time}>
            Posted on {product?.createdtime ? new Date(product.createdtime).toLocaleString() : "-"}{" "}
            <span style={styles.timeAgo}>
              {product?.createdtime ? `(${getTimeAgo(product.createdtime)})` : ""}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;

/* 🔹 STYLES (INLINE FOR SINGLE FILE USAGE) */
const styles = {
  page: {
    backgroundColor: "#f1f3f6",
    minHeight: "100vh",
    padding: "30px",
  },
  container: {
    maxWidth: "1200px",
    margin: "auto",
    background: "#fff",
    display: "flex",
    gap: "40px",
    padding: "30px",
    borderRadius: "6px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
  },
  left: {
    width: "40%",
  },
  imageWrapper: {
    position: "relative",
    border: "1px solid #ddd",
    borderRadius: "6px",
    padding: "15px",
  },
  image: {
    width: "100%",
    objectFit: "contain",
    borderRadius: "6px",
  },
  icons: {
    position: "absolute",
    top: "15px",
    right: "15px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  icon: {
    background: "#fff",
    padding: "10px",
    borderRadius: "50%",
    cursor: "pointer",
    boxShadow: "0 1px 5px rgba(0,0,0,0.2)",
  },
  sharePopup: {
    position: "absolute",
    top: "40px",
    right: 0,
    background: "#fff",
    border: "1px solid #e0e0e0",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    padding: "8px",
    minWidth: "160px",
    zIndex: 5,
  },
  shareItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    background: "transparent",
    border: "none",
    padding: "8px",
    borderRadius: "6px",
    cursor: "pointer",
    textAlign: "left",
  },
  shareText: {
    fontSize: "14px",
    color: "#333",
  },
  dotsRow: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    marginTop: "12px",
  },
  dot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
  },
  carouselArrowLeft: {
    position: "absolute",
    top: "50%",
    left: "8px",
    transform: "translateY(-50%)",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    cursor: "pointer",
  },
  carouselArrowRight: {
    position: "absolute",
    top: "50%",
    right: "8px",
    transform: "translateY(-50%)",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    cursor: "pointer",
  },
  buttonGroup: {
    display: "flex",
    gap: "15px",
    marginTop: "20px",
  },
  button: {
    flex: 1,
    padding: "14px",
    fontSize: "16px",
    fontWeight: "bold",
    cursor: "pointer",
    border: "none",
    borderRadius: "4px",
  },
  cartBtn: {
    background: "#ff9f00",
    color: "#fff",
  },
  buyBtn: {
    background: "#fb641b",
    color: "#fff",
  },
  right: {
    width: "60%",
  },
  title: {
    fontSize: "26px",
    marginBottom: "15px",
  },
  price: {
    fontSize: "24px",
    color: "#388e3c",
    fontWeight: "bold",
    marginBottom: "15px",
  },
  quantity: {
    fontSize: "16px",
    color: "#475569",
    fontWeight: "600",
    marginBottom: "10px",
  },
  desc: {
    fontSize: "16px",
    color: "#333",
    marginBottom: "15px",
  },
  phoneButton: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "16px",
    padding: 0,
  },
  mobile: {
    fontSize: "15px",
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
  },
  time: {
    fontSize: "14px",
    color: "#555",
  },
  timeAgo: {
    color: "#878787",
    marginLeft: "6px",
  },
  center: {
    textAlign: "center",
    marginTop: "100px",
    fontSize: "18px",
  },
  error: {
    textAlign: "center",
    marginTop: "100px",
    color: "red",
  },
};
